import { Terminal, Maximize2, Minimize2, ChevronDown, Trash2 } from 'lucide-react';
import { useImageStore } from '@/entities/image/store';

type LoggerMode = 'collapsed' | 'split' | 'maximized' | 'hidden';

interface DebugLoggerProps {
  mode?: LoggerMode;
  onModeChange?: (mode: LoggerMode) => void;
}

export default function DebugLogger({ mode = 'hidden', onModeChange }: DebugLoggerProps) {
  const debugLogs = useImageStore((s) => s.debugLogs);
  const addLog = useImageStore((s) => s.addLog);

  const setMode = (m: LoggerMode) => {
    onModeChange?.(m);
  };

  if (mode === 'hidden') return null;

  if (mode === 'collapsed') {
    return (
      <div className="flex items-center justify-end p-1 bg-gray-900 border-t border-gray-700 shrink-0">
        <button
          onClick={() => setMode('split')}
          className="px-2 py-1 flex items-center gap-2 text-xs text-gray-400 hover:text-green-400 hover:bg-gray-800 rounded transition"
        >
          <Terminal className="w-3 h-3" />
          <span>Debug Logger</span>
        </button>
      </div>
    );
  }

  const isMaximized = mode === 'maximized';

  return (
    <div className={`flex flex-col bg-gray-900 text-green-400 font-mono text-xs shrink-0 ${isMaximized ? 'flex-1 min-h-0 border-b-0' : 'h-[35vh] border-t border-gray-700'}`}>
      <div className="flex items-center justify-between px-2 py-1 bg-gray-950 border-b border-gray-800">
        <div className="flex items-center space-x-2">
          <Terminal className="w-4 h-4" />
          <span className="font-bold">Debug Logger</span>
          <span className="text-gray-600">({debugLogs.length})</span>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => { useImageStore.setState({ debugLogs: [] }); addLog('Cleared'); }}
            className="p-1 hover:bg-gray-800 rounded text-gray-500 hover:text-red-400 transition"
            title="Clear Logs"
          >
            <Trash2 className="w-3 h-3" />
          </button>
          {isMaximized ? (
            <button
              onClick={() => setMode('split')}
              className="p-1 hover:bg-gray-800 rounded text-gray-500 hover:text-green-400 transition"
              title="Minimize to Split"
            >
              <Minimize2 className="w-3 h-3" />
            </button>
          ) : (
            <button
              onClick={() => setMode('maximized')}
              className="p-1 hover:bg-gray-800 rounded text-gray-500 hover:text-green-400 transition"
              title="Maximize Logger"
            >
              <Maximize2 className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={() => setMode('collapsed')}
            className="p-1 hover:bg-gray-800 rounded text-gray-500 hover:text-white transition"
            title="Collapse Logger"
          >
            <ChevronDown className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-2 space-y-1">
        {debugLogs.length === 0 ? (
          <div className="text-gray-600">No logs yet...</div>
        ) : (
          debugLogs.map((log, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-gray-600 shrink-0 select-none">{log.time}</span>
              <span className="break-all">{log.msg}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
